/**
 * TransitionContext
 * ─────────────────
 * Seitenwechsel ohne Vollbild-Wisch (Davids Wunsch 08.10.2026: die schwarze Fläche
 * mit Logo über den ganzen Bildschirm war „toll, aber nervig“, gut eine Sekunde pro Klick).
 *
 * Jetzt:
 *   - navigateWith(path) wechselt sofort; die Seiten blenden selbst kurz aus und ein
 *     (gemeinsame Werte in components/animations/seitenBlende.js, unter einer halben Sekunde).
 *   - Bei JEDEM Seitenwechsel (auch normale Links) läuft oben eine dünne orange Linie
 *     einmal quer durch, wie ein Ladebalken. Nichts verdeckt den Inhalt.
 *
 * Die Schnittstelle bleibt gleich (navigateWith / wipeWith, useTransitionNavigate,
 * useTransitionWipe, TransitionFader), damit die Aufrufer nichts ändern müssen. Die Kopfleiste
 * nutzt inzwischen echte Links; navigateWith bleibt für Sprünge aus dem Code (z. B. Produktseite → All-in-One).
 * Die Richtungsangabe ('left', 'down' …) wird angenommen und ignoriert.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const TransitionContext = createContext(null);

export function TransitionProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateWith = useCallback((to) => {
    if (location.pathname === to) return;
    navigate(to);
  }, [navigate, location.pathname]);

  // Früher lief die Aktion hinter der Wischfläche; jetzt einfach sofort.
  const wipeWith = useCallback((action) => {
    if (typeof action === 'function') action();
  }, []);

  return (
    <TransitionContext.Provider value={{ navigateWith, wipeWith, phase: 'idle', direction: 'left' }}>
      {children}
      <Ladelinie pfad={location.pathname} />
    </TransitionContext.Provider>
  );
}

// Dünne orange Linie oben: wächst von links nach rechts und blendet aus (ca. 0,5 s).
// Läuft nicht beim ersten Laden, nur bei Seitenwechseln; ohne Bewegung (reduced motion) gar nicht.
function Ladelinie({ pfad }) {
  const [lauf, setLauf] = useState(0);
  const erster = useRef(true);
  useEffect(() => {
    if (erster.current) { erster.current = false; return; }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setLauf(n => n + 1);
  }, [pfad]);
  if (!lauf) return null;
  return (
    <div key={lauf} aria-hidden="true" className="seiten-ladelinie" style={{
      position: 'fixed', left: 0, top: 0, right: 0, height: 2, zIndex: 9000, pointerEvents: 'none',
      background: 'linear-gradient(90deg, rgba(249,115,22,0) 0%, #F97316 30%, #FFB27A 100%)',
      boxShadow: '0 0 12px rgba(249,115,22,.7)', transformOrigin: '0 50%',
      animation: 'seiten-ladelinie .55s cubic-bezier(.22,1,.36,1) forwards',
    }} />
  );
}

/** Früher dimmte das den Inhalt während des Wischs; bleibt als Durchreiche für App.js. */
export function TransitionFader({ children }) {
  return children;
}

/** (path, direction) → Seite wechseln. Richtung wird ignoriert. */
export function useTransitionNavigate() {
  const ctx = useContext(TransitionContext);
  const navigate = useNavigate();
  return ctx ? ctx.navigateWith : navigate;
}

/** (action) → Aktion sofort ausführen. */
export function useTransitionWipe() {
  const ctx = useContext(TransitionContext);
  if (!ctx) return (action) => { if (typeof action === 'function') action(); };
  return ctx.wipeWith;
}
